/**
 * Technician Project Service
 *
 * Wrapper around projectService that filters out financial information
 * for technician users. Provides project access with restricted data visibility.
 *
 * @module services/technicianProjectService
 */

import { supabase } from './supabase';
import { terminoParaFiltro, columnaNormalizada } from '../utils/normalizacionBusqueda';
import { TASK_STATUS, PROJECT_STATUS, PAGINATION } from '../utils/constants';
// #332: `progress-photos` is a private bucket and `photo_url` stores the object
// path, not a working URL. Every read has to re-sign. Reuse the one signer that
// already lives in progressService instead of minting a second one here.
import { resolvePhotoUrls } from './progressService';

/**
 * Get task IDs assigned to a user via task_assignments, then verify
 * the user has at least one task in the given project.
 * @param {string} userId - User ID
 * @param {string} projectId - Project ID
 * @returns {Promise<{userTaskIds: string[], hasAccess: boolean}>}
 */
const verifyProjectAccess = async (userId, projectId) => {
  const { data: assignments, error } = await supabase
    .from('task_assignments')
    .select('task_id')
    .eq('user_id', userId);

  if (error) throw error;

  const userTaskIds = assignments?.map(a => a.task_id) || [];

  if (userTaskIds.length === 0) return { userTaskIds, hasAccess: false };

  const { data: projectTasks, error: ptError } = await supabase
    .from('tasks')
    .select('id')
    .eq('project_id', projectId)
    .in('id', userTaskIds)
    .limit(1);

  if (ptError) throw ptError;

  return { userTaskIds, hasAccess: (projectTasks?.length || 0) > 0 };
};

/**
 * Get projects where the technician has assigned tasks
 * @param {string} userId - Technician user ID
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Project status filter
 * @param {string} filters.search - Search term
 * @param {number} filters.page - Page number
 * @param {number} filters.pageSize - Items per page
 * @returns {Promise<{data, count, error}>}
 */
export const getAssignedProjects = async (userId, filters = {}) => {
  try {
    // First, get task IDs assigned to this user via task_assignments
    const { data: assignments, error: assignError } = await supabase
      .from('task_assignments')
      .select('task_id')
      .eq('user_id', userId);

    if (assignError) throw assignError;

    const assignedTaskIds = assignments?.map(a => a.task_id) || [];

    if (assignedTaskIds.length === 0) {
      return { data: [], count: 0, error: null };
    }

    // Get project IDs from those tasks
    const { data: taskProjects, error: taskError } = await supabase
      .from('tasks')
      .select('project_id')
      .in('id', assignedTaskIds);

    if (taskError) throw taskError;

    // Get unique project IDs
    const projectIds = [...new Set(taskProjects?.map(t => t.project_id) || [])];

    if (projectIds.length === 0) {
      return { data: [], count: 0, error: null };
    }

    // Fetch projects (without financial data)
    let query = supabase
      .from('projects')
      .select(`
        id,
        project_number,
        title,
        description,
        status,
        po_number,
        estimated_payment_date,
        created_at,
        updated_at,
        client:clients!projects_client_id_fkey(
          id,
          company_name,
          primary_contact_name,
          primary_contact_phone
        )
      `, { count: 'exact' })
      .in('id', projectIds);

    // Apply filters
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    if (filters.search) {
      // #742/#739 — el termino sale del modulo compartido: normaliza tildes,
      // saca los metacaracteres de PostgREST (`,` `(` `)` son sintaxis dentro de
      // un `.or()`) y escapa los comodines de LIKE. `title` se compara contra su
      // columna generada `title_norm`; `project_number` se queda crudo porque es
      // un codigo, no texto humano — no tiene tildes que normalizar y el ILIKE ya
      // es insensible a mayusculas.
      const termino = terminoParaFiltro(filters.search);
      query = query.or(`project_number.ilike.%${termino}%,${columnaNormalizada('title')}.ilike.%${termino}%`);
    }

    // Apply pagination
    const page = filters.page || 0;
    const pageSize = filters.pageSize || PAGINATION.DEFAULT_PAGE_SIZE;
    const from = page * pageSize;
    const to = from + pageSize - 1;

    query = query
      .range(from, to)
      .order('created_at', { ascending: false });

    const { data, count, error } = await query;

    if (error) throw error;

    // Add task summary for each project
    const projectsWithTasks = await Promise.all(
      data.map(async (project) => {
        // Get tasks in this project that are assigned to the user
        const projectTaskIds = assignedTaskIds.length > 0
          ? assignedTaskIds
          : [];

        const { data: tasks } = await supabase
          .from('tasks')
          .select('id, status')
          .eq('project_id', project.id)
          .in('id', projectTaskIds);

        const taskSummary = {
          total: tasks?.length || 0,
          pending: tasks?.filter(t => t.status === TASK_STATUS.PENDING).length || 0,
          inProgress: tasks?.filter(t => t.status === TASK_STATUS.IN_PROGRESS).length || 0,
          completed: tasks?.filter(t => t.status === TASK_STATUS.COMPLETED).length || 0,
        };

        return {
          ...project,
          taskSummary,
        };
      })
    );

    return { data: projectsWithTasks, count, error: null };
  } catch (error) {
    console.error('[TECHNICIAN_PROJECT] Get assigned projects error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Get project detail without financial information
 * @param {string} projectId - Project ID
 * @returns {Promise<{data, error}>}
 */
export const getProjectDetail = async (projectId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Get task IDs assigned to this user in this project
    const { data: userAssignments, error: assignError } = await supabase
      .from('task_assignments')
      .select('task_id')
      .eq('user_id', user.id);

    if (assignError) throw assignError;

    const userTaskIds = userAssignments?.map(a => a.task_id) || [];

    // Verify user has tasks in this project
    const { data: userTasks, error: accessError } = await supabase
      .from('tasks')
      .select('id')
      .eq('project_id', projectId)
      .in('id', userTaskIds.length > 0 ? userTaskIds : ['00000000-0000-0000-0000-000000000000'])
      .limit(1);

    if (accessError) throw accessError;

    if (!userTasks || userTasks.length === 0) {
      throw new Error('No tiene acceso a este proyecto');
    }

    // Fetch project without financial fields
    const { data: project, error } = await supabase
      .from('projects')
      .select(`
        id,
        project_number,
        title,
        description,
        status,
        po_number,
        estimated_payment_date,
        created_at,
        updated_at,
        client:clients!projects_client_id_fkey(
          id,
          company_name,
          primary_contact_name,
          primary_contact_phone,
          primary_contact_email
        ),
        created_by_profile:profiles!projects_created_by_fkey(
          id,
          full_name
        )
      `)
      .eq('id', projectId)
      .single();

    if (error) throw error;

    // Get all tasks for this project assigned to user
    const { data: tasks, error: tasksError } = await supabase
      .from('tasks')
      .select(`
        id,
        task_number,
        title,
        description,
        status,
        priority,
        category,
        start_date,
        due_date,
        estimated_hours,
        actual_hours
      `)
      .eq('project_id', projectId)
      .in('id', userTaskIds.length > 0 ? userTaskIds : ['00000000-0000-0000-0000-000000000000'])
      .order('due_date', { ascending: true });

    if (tasksError) throw tasksError;

    // Calculate task summary
    const taskSummary = {
      total: tasks?.length || 0,
      pending: tasks?.filter(t => t.status === TASK_STATUS.PENDING).length || 0,
      inProgress: tasks?.filter(t => t.status === TASK_STATUS.IN_PROGRESS).length || 0,
      completed: tasks?.filter(t => t.status === TASK_STATUS.COMPLETED).length || 0,
      blocked: tasks?.filter(t => t.status === TASK_STATUS.BLOCKED).length || 0,
      totalEstimatedHours: tasks?.reduce((sum, t) => sum + (t.estimated_hours || 0), 0) || 0,
      totalActualHours: tasks?.reduce((sum, t) => sum + (t.actual_hours || 0), 0) || 0,
    };

    return {
      data: {
        ...project,
        tasks,
        taskSummary,
      },
      error: null,
    };
  } catch (error) {
    console.error('[TECHNICIAN_PROJECT] Get project detail error:', error);
    return { data: null, error };
  }
};

/**
 * Get project timeline (bitacora) events
 * @param {string} projectId - Project ID
 * @param {Object} filters - Filter options
 * @param {string} filters.eventType - Filter by event type
 * @param {string} filters.dateFrom - Start date
 * @param {string} filters.dateTo - End date
 * @returns {Promise<{data, error}>}
 */
export const getProjectTimeline = async (projectId, filters = {}) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { hasAccess } = await verifyProjectAccess(user.id, projectId);
    if (!hasAccess) {
      throw new Error('No tiene acceso a este proyecto');
    }

    let query = supabase
      .from('timeline_events')
      .select(`
        id,
        event_type,
        title,
        description,
        event_date,
        metadata,
        photos,
        is_milestone,
        created_at,
        created_by_profile:profiles!created_by(id, full_name)
      `)
      .eq('project_id', projectId);

    if (filters.eventType) {
      query = query.eq('event_type', filters.eventType);
    }

    if (filters.dateFrom) {
      query = query.gte('event_date', filters.dateFrom);
    }

    if (filters.dateTo) {
      query = query.lte('event_date', filters.dateTo);
    }

    query = query.order('event_date', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[TECHNICIAN_PROJECT] Get timeline error:', error);
    return { data: null, error };
  }
};

/**
 * Get project documents and manuals
 * @param {string} projectId - Project ID
 * @param {Object} filters - Filter options
 * @param {string} filters.category - Document category
 * @returns {Promise<{data, error}>}
 */
export const getProjectDocuments = async (projectId, filters = {}) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { hasAccess } = await verifyProjectAccess(user.id, projectId);
    if (!hasAccess) {
      throw new Error('No tiene acceso a este proyecto');
    }

    let query = supabase
      .from('project_documents')
      .select(`
        id,
        name,
        description,
        category,
        file_url,
        file_type,
        file_size,
        created_at,
        uploaded_by_profile:profiles!uploaded_by(id, full_name)
      `)
      .eq('project_id', projectId)
      .eq('is_visible_to_technicians', true); // Only show documents marked visible

    if (filters.category) {
      query = query.eq('category', filters.category);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[TECHNICIAN_PROJECT] Get documents error:', error);
    return { data: null, error };
  }
};

/**
 * Get project progress photos
 * @param {string} projectId - Project ID
 * @param {Object} filters - Filter options
 * @param {string} filters.taskId - Filter by task
 * @returns {Promise<{data, error}>}
 */
export const getProjectPhotos = async (projectId, filters = {}) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { hasAccess } = await verifyProjectAccess(user.id, projectId);
    if (!hasAccess) {
      throw new Error('No tiene acceso a este proyecto');
    }

    let query = supabase
      .from('progress_photos')
      .select(`
        id,
        photo_url,
        photo_path,
        caption,
        gps_latitude,
        gps_longitude,
        uploaded_at,
        task:tasks(id, title, task_number),
        uploaded_by_profile:profiles!uploaded_by(id, full_name)
      `)
      .eq('project_id', projectId);

    if (filters.taskId) {
      query = query.eq('task_id', filters.taskId);
    }

    query = query.order('uploaded_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    // #332: re-sign on read. Without this the technician portal renders the raw
    // stored value — a bare object path or a long-expired signed URL — against a
    // private bucket, so every <img> is a broken image.
    const resolved = await resolvePhotoUrls(data);

    return { data: resolved, error: null };
  } catch (error) {
    console.error('[TECHNICIAN_PROJECT] Get photos error:', error);
    return { data: null, error };
  }
};

/**
 * Get project team members (without financial roles)
 * @param {string} projectId - Project ID
 * @returns {Promise<{data, error}>}
 */
export const getProjectTeam = async (projectId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { hasAccess } = await verifyProjectAccess(user.id, projectId);
    if (!hasAccess) {
      throw new Error('No tiene acceso a este proyecto');
    }

    // Get all tasks in this project
    const { data: projectTasks, error: ptError } = await supabase
      .from('tasks')
      .select('id')
      .eq('project_id', projectId);

    if (ptError) throw ptError;

    const taskIds = projectTasks?.map(t => t.id) || [];

    // Get all assignees for those tasks via task_assignments
    const { data: taskAssignees, error: taskError } = taskIds.length > 0
      ? await supabase
          .from('task_assignments')
          .select(`
            user_id,
            assigned_user:profiles!task_assignments_user_id_fkey(
              id,
              full_name,
              phone,
              avatar_url,
              role
            )
          `)
          .in('task_id', taskIds)
      : { data: [], error: null };

    if (taskError) throw taskError;

    // Deduplicate team members
    const teamMap = new Map();
    taskAssignees?.forEach(assignment => {
      if (assignment.assigned_user) {
        teamMap.set(assignment.assigned_user.id, assignment.assigned_user);
      }
    });

    // Get project manager (created_by)
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select(`
        created_by_profile:profiles!projects_created_by_fkey(
          id,
          full_name,
          phone,
          avatar_url,
          role
        )
      `)
      .eq('id', projectId)
      .single();

    if (!projectError && project.created_by_profile) {
      teamMap.set(project.created_by_profile.id, {
        ...project.created_by_profile,
        isProjectManager: true,
      });
    }

    return { data: Array.from(teamMap.values()), error: null };
  } catch (error) {
    console.error('[TECHNICIAN_PROJECT] Get team error:', error);
    return { data: null, error };
  }
};

/**
 * Get project summary for dashboard widget
 * @param {string} userId - Technician user ID
 * @returns {Promise<{data, error}>}
 */
export const getProjectsSummary = async (userId) => {
  try {
    // Get task IDs assigned to user via task_assignments
    const { data: assignments, error: assignError } = await supabase
      .from('task_assignments')
      .select('task_id')
      .eq('user_id', userId);

    if (assignError) throw assignError;

    const assignedTaskIds = assignments?.map(a => a.task_id) || [];

    if (assignedTaskIds.length === 0) {
      return {
        data: {
          totalProjects: 0,
          activeProjects: 0,
          totalTasks: 0,
          pendingTasks: 0,
          inProgressTasks: 0,
          completedTasks: 0,
        },
        error: null,
      };
    }

    // Get those tasks with project_id and status
    const { data: taskProjects, error: taskError } = await supabase
      .from('tasks')
      .select('project_id, status')
      .in('id', assignedTaskIds);

    if (taskError) throw taskError;

    const projectIds = [...new Set(taskProjects?.map(t => t.project_id) || [])];

    if (projectIds.length === 0) {
      return {
        data: {
          totalProjects: 0,
          activeProjects: 0,
          totalTasks: 0,
          pendingTasks: 0,
          inProgressTasks: 0,
          completedTasks: 0,
        },
        error: null,
      };
    }

    // Get project statuses
    const { data: projects, error: projectError } = await supabase
      .from('projects')
      .select('id, status')
      .in('id', projectIds);

    if (projectError) throw projectError;

    const summary = {
      totalProjects: projects?.length || 0,
      activeProjects: projects?.filter(p =>
        p.status === PROJECT_STATUS.IN_PROGRESS || p.status === PROJECT_STATUS.PO_RECEIVED
      ).length || 0,
      totalTasks: taskProjects?.length || 0,
      pendingTasks: taskProjects?.filter(t => t.status === TASK_STATUS.PENDING).length || 0,
      inProgressTasks: taskProjects?.filter(t => t.status === TASK_STATUS.IN_PROGRESS).length || 0,
      completedTasks: taskProjects?.filter(t => t.status === TASK_STATUS.COMPLETED).length || 0,
    };

    return { data: summary, error: null };
  } catch (error) {
    console.error('[TECHNICIAN_PROJECT] Get summary error:', error);
    return { data: null, error };
  }
};

/**
 * Get recent activity for technician's projects
 * @param {string} userId - Technician user ID
 * @param {number} limit - Maximum items to return
 * @returns {Promise<{data, error}>}
 */
export const getRecentActivity = async (userId, limit = 10) => {
  try {
    // Get task IDs assigned to user via task_assignments
    const { data: assignments, error: assignError } = await supabase
      .from('task_assignments')
      .select('task_id')
      .eq('user_id', userId);

    if (assignError) throw assignError;

    const assignedTaskIds = assignments?.map(a => a.task_id) || [];

    if (assignedTaskIds.length === 0) {
      return { data: [], error: null };
    }

    // Get project IDs from those tasks
    const { data: taskProjects, error: taskError } = await supabase
      .from('tasks')
      .select('project_id')
      .in('id', assignedTaskIds);

    if (taskError) throw taskError;

    const projectIds = [...new Set(taskProjects?.map(t => t.project_id) || [])];

    if (projectIds.length === 0) {
      return { data: [], error: null };
    }

    // Get recent timeline events from user's projects
    const { data, error } = await supabase
      .from('timeline_events')
      .select(`
        id,
        event_type,
        title,
        description,
        event_date,
        is_milestone,
        project:projects(id, project_number, title),
        created_by_profile:profiles!created_by(id, full_name)
      `)
      .in('project_id', projectIds)
      .order('event_date', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[TECHNICIAN_PROJECT] Get recent activity error:', error);
    return { data: null, error };
  }
};

const technicianProjectService = {
  getAssignedProjects,
  getProjectDetail,
  getProjectTimeline,
  getProjectDocuments,
  getProjectPhotos,
  getProjectTeam,
  getProjectsSummary,
  getRecentActivity,
};

export default technicianProjectService;
